import React, { useEffect, useState } from "react";
import { useLoader } from "../../../../base/context/LoaderProvider";
import {
  ToastTypes,
  useMessage,
} from "../../../../base/context/MessageProvider";
import { orderApiService } from "../../../../service/seller/orderApiService";
import { WalletApiService } from "../../../../service/seller/walletApiService";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate } from "../../../../helper/utils";
import { getImPointsDiscount, getFinalPayableAfterIm } from "../../../../helper/orderImPoints";
import { 
  FiPackage, FiTruck, FiMapPin, FiPhone, FiCreditCard, FiCheckCircle, 
  FiClock, FiAlertCircle, FiChevronDown, FiChevronUp, FiArrowLeft,
  FiShoppingBag, FiFileText
} from "react-icons/fi";
import { GoCheckCircleFill } from "react-icons/go";
import RaiseTicket from "../../../../component/views/seller/orderDetails/raiseTicketPopup";
import PaymentModal from "../../../../component/views/seller/orders/paymentModal";
import CancelOrderModal from "../../../../component/views/seller/orders/cancelOrderModal";
import TicketChatPanel from "../../../../component/library/ticketChatPanel";
import "./orderDetailNew.css";
import { getOrderItemPricingMode } from "../../../../utils/orderPricingMode";

const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const PLACEHOLDER_IMAGE = '/placeholder-image.svg';

export default function OrderDetailNew() {
  const [data, setData] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);
  
  const [ticketPopup, setTicketPopup] = useState(false);
  const [ticketPanelTicketId, setTicketPanelTicketId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isPayingOrder, setIsPayingOrder] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const loader = useLoader();
  const params = useParams();
  const message = useMessage();
  const navigate = useNavigate();

  const getOrderById = () => {
    loader.showLoading(true);
    orderApiService
      .getOrderById(params.id)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setData(res.data);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      });
  };

  const fetchWalletBalance = async () => {
    try {
      const res = await WalletApiService.getWalet();
      if (res.status.code === 200) {
        let balance = res.data?.balance ?? res.data?.wallet?.balance ?? res.data ?? 0;
        setWalletBalance(Number(balance) || 0);
      }
    } catch (err) {
      console.error("Failed to fetch wallet:", err);
    }
  };

  useEffect(() => {
    getOrderById();
    fetchWalletBalance();
  }, []);

  // Pricing calculation - same logic as admin panel
  const calculatePricing = () => {
    const shippingCharges = Number(data?.totalShippingCharges) || 0;
    
    // Build a map of productId -> wholesale FBP (same as admin panel)
    const wholesaleBreakdownMap = new Map();
    if (Array.isArray(data?.breakdown)) {
      for (const breakdownItem of data.breakdown) {
        if (breakdownItem?.pricingMode === 'wholesale' && breakdownItem?.productId && breakdownItem?.breakdown?.[0]?.FBP) {
          wholesaleBreakdownMap.set(breakdownItem.productId.toString(), Number(breakdownItem.breakdown[0].FBP) || 0);
        }
      }
    }
    
    // Check if this is a manual order (marketplace === 'manual' or createdVia === 'manual_admin')
    const isManualOrder = data?.marketplace?.toLowerCase() === 'manual' || data?.createdVia === 'manual_admin';
    
    // Helper function to determine pricing mode for an item (same as admin panel)
    const getPricingMode = (detail) =>
      getOrderItemPricingMode(detail, {
        orderBreakdown: data?.breakdown || [],
        orderType: isManualOrder ? data?.orderType : null,
        isManualOrder,
        dropshippingLabel: "one-piece",
      });
    
    // Helper function to get price for one-piece item - prioritize finalPrice over breakdown.FBP
    const getOnePiecePrice = (detail) => {
      const variant = detail?.variant;
      // Priority: finalPrice > breakdown.FBP
      if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
        return Number(variant.finalPrice);
      }
      const breakdown = variant?.breakdown;
      return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
    };
    
    // Helper function to get price for wholesale item - prioritize wholesaleFinalPrice over breakdown
    const getWholesalePrice = (detail) => {
      const variant = detail?.variant;
      // Priority: wholesaleFinalPrice > breakdown.FBP
      if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) {
        return Number(variant.wholesaleFinalPrice);
      }
      const productId = detail?.product?._id?.toString() || detail?.product?.toString() || '';
      const wholesaleFBP = wholesaleBreakdownMap.get(productId);
      if (wholesaleFBP) {
        return wholesaleFBP;
      }
      const breakdown = variant?.breakdown;
      return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
    };
    
    // Calculate product total - separate one-piece and wholesale, then sum (same as admin panel)
    let productTotal = 0;
    let onePieceTotal = 0;
    let wholesaleTotal = 0;
    
    // Group orderDetails by product to calculate wholesale totals per product
    const productGroups = new Map();
    (data?.orderDetails || []).forEach(detail => {
      const productId = detail?.product?._id?.toString() || detail?.product?.toString() || '';
      if (!productGroups.has(productId)) {
        productGroups.set(productId, []);
      }
      productGroups.get(productId).push(detail);
    });
    
    const isOrderWholesale = isManualOrder && data?.orderType?.toLowerCase() === 'wholesale';

    // Calculate totals for each product group
    productGroups.forEach((details, productId) => {
      let productOnePieceTotal = 0;
      let productWholesaleQuantity = 0;
      let productWholesaleTotal = 0;
      
      // Calculate one-piece and wholesale separately for this product
      details.forEach(detail => {
        const pricingMode = getPricingMode(detail);
        const quantity = Number(detail?.quantity) || 0;
        
        if (pricingMode === 'one-piece') {
          const price = getOnePiecePrice(detail);
          productOnePieceTotal += price * quantity;
        } else {
          // For wholesale, calculate total from individual items if manual order type is wholesale
          if (isManualOrder && isOrderWholesale) {
            const price = getWholesalePrice(detail);
            productWholesaleTotal += price * quantity;
          }
          productWholesaleQuantity += quantity;
        }
      });
      
      // Add one-piece total
      onePieceTotal += productOnePieceTotal;
      
      // Add wholesale total
      if (isManualOrder && isOrderWholesale && productWholesaleTotal > 0) {
        wholesaleTotal += productWholesaleTotal;
      } else {
        const wholesaleFBP = wholesaleBreakdownMap.get(productId);
        if (wholesaleFBP && productWholesaleQuantity > 0) {
          wholesaleTotal += wholesaleFBP;
        }
      }
    });
    
    productTotal = onePieceTotal + wholesaleTotal;
    const totalWithShipping = productTotal + shippingCharges;

    // Payment Breakdown logic
    const pb = data?.paymentBreakdown;
    const hasNewBreakdown = pb && typeof pb === 'object';
    const breakdownSum = (
      (Number(pb?.imPoints?.amount) || 0) + 
      (Number(pb?.wallet?.amount) || 0) + 
      (Number(pb?.razorpay?.amount) || 0) + 
      (Number(pb?.ccavenue?.amount) || 0) + 
      (Number(pb?.bankTransfer?.amount) || 0)
    );

    const imPointsAmount = hasNewBreakdown ? (Number(pb.imPoints?.amount) || 0) : (Number(data?.imPointsDiscount) || 0);
    const walletAmount = hasNewBreakdown ? (Number(pb.wallet?.amount) || 0) : 0;
    const razorpayAmount = hasNewBreakdown ? (Number(pb.razorpay?.amount) || 0) : 0;
    const ccavenueAmount = hasNewBreakdown ? (Number(pb.ccavenue?.amount) || 0) : 0;
    const bankTransferAmount = hasNewBreakdown ? (Number(pb.bankTransfer?.amount) || 0) : 0;

    const totalOrderValue = data?.grossOrderAmount || 
                           (hasNewBreakdown && breakdownSum > 0 ? breakdownSum : totalWithShipping);

    return {
      productTotal,
      shippingCharges,
      totalOrderValue,
      imPointsAmount,
      walletAmount,
      razorpayAmount,
      ccavenueAmount,
      bankTransferAmount,
      hasNewBreakdown,
      totalWithShipping,
      finalAmount: totalWithShipping - (Number(data?.imPointsDiscount) || 0)
    };
  };

  const pricing = calculatePricing();

  const getStatusConfig = (status) => {
    const configs = {
      'payment pending': { color: '#f59e0b', bg: '#fef3c7', icon: FiClock, label: 'Payment Pending' },
      'processing': { color: '#3b82f6', bg: '#dbeafe', icon: FiPackage, label: 'Processing' },
      'shipped': { color: '#8b5cf6', bg: '#ede9fe', icon: FiTruck, label: 'Shipped' },
      'partially-shipped': { color: '#8b5cf6', bg: '#ede9fe', icon: FiTruck, label: 'Partially Shipped' },
      'delivered': { color: '#10b981', bg: '#d1fae5', icon: FiCheckCircle, label: 'Delivered' },
      'cancelled': { color: '#ef4444', bg: '#fee2e2', icon: FiAlertCircle, label: 'Cancelled' },
      'refunded': { color: '#6b7280', bg: '#f3f4f6', icon: FiCreditCard, label: 'Refunded' },
      'abnormal': { color: '#ef4444', bg: '#fee2e2', icon: FiAlertCircle, label: 'Abnormal' },
    };
    return configs[status?.toLowerCase()] || configs['processing'];
  };

  const statusConfig = getStatusConfig(data?.status);

  const isWithin30Minutes = () => {
    if (!data?.paidDate) return false;
    return (new Date() - new Date(data.paidDate)) / (1000 * 60) <= 30;
  };

  const canCancelProcessingOrder = () => data.status === "processing" && isWithin30Minutes();
  
  const canRaiseTicket = () => {
    if (data?.ticket && data?.ticketId) return false;
    if (["shipped", "partially-shipped", "delivered"].includes(data.status)) return true;
    if (data.status === "processing" && !isWithin30Minutes()) return true;
    return false;
  };

  // Payment handlers
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const openRazorpayCheckout = async (razorpayData, orderId) => {
    if (!await loadRazorpayScript()) {
      setIsPayingOrder(false);
      message.showToast('Failed to load Razorpay', ToastTypes.ERROR);
      return;
    }
    new window.Razorpay({
      key: process.env.REACT_APP_RAZOR_PAY_KEY,
      amount: razorpayData.amount,
      currency: razorpayData.currency || 'INR',
      order_id: razorpayData.razorpayOrderId,
      name: 'Importerr',
      description: `Payment for Order ${razorpayData.upsaleoOrderId}`,
      handler: async (response) => {
        try {
          const verifyRes = await orderApiService.verifyRazorpayPayment({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            orderId
          });
          setIsPayingOrder(false);
          if (verifyRes.status.code === 200) {
            message.showToast('Payment successful!', ToastTypes.SUCCESS);
            setPaymentModalOpen(false);
            getOrderById();
          } else {
            message.showToast(verifyRes.error?.message || 'Verification failed', ToastTypes.ERROR);
          }
        } catch {
          setIsPayingOrder(false);
          message.showToast('Verification failed', ToastTypes.ERROR);
        }
      },
      modal: { ondismiss: () => { setIsPayingOrder(false); message.showToast('Payment cancelled', ToastTypes.WARNING); } },
      theme: { color: '#10b981' }
    }).open();
  };

  const handlePaymentConfirm = async (_, paymentMethod) => {
    setIsPayingOrder(true);
    try {
      const res = await orderApiService.PayOrder(data._id, paymentMethod);
      if (paymentMethod === 'razorpay' && res.status.code === 200 && res.data?.razorpayOrderId) {
        await openRazorpayCheckout(res.data, data._id);
        return;
      }
      setIsPayingOrder(false);
      if (res.status.code === 200) {
        message.showToast(res.status.message || "Payment successful", ToastTypes.SUCCESS);
        setPaymentModalOpen(false);
        getOrderById();
      } else {
        message.showToast(res.error?.message || "Payment failed", ToastTypes.ERROR);
      }
    } catch (err) {
      setIsPayingOrder(false);
      message.showToast(err?.message || "Server error", ToastTypes.ERROR);
    }
  };

  const handleCancelConfirm = async () => {
    setIsCancelling(true);
    try {
      const res = await orderApiService.CancelOrder(data._id);
      setIsCancelling(false);
      setCancelModalOpen(false);
      if (res.status.code === 200) {
        message.showToast(res.status.message || "Order cancelled", ToastTypes.SUCCESS);
        getOrderById();
      } else {
        message.showToast(res.error?.message || "Failed to cancel", ToastTypes.ERROR);
      }
    } catch (err) {
      setIsCancelling(false);
      message.showToast(err?.message || "Server error", ToastTypes.ERROR);
    }
  };

  const toggleProduct = (id) => setExpandedProducts(prev => ({ ...prev, [id]: !prev[id] }));

  // Determine shipping address based on shipping method
  const shippingAddress = data?.shippingMethod === 'self-pickup' && data?.warehouseName
    ? [
        data?.warehouseName,
        data?.warehouseAddress1,
        data?.warehouseAddress2,
        data?.warehouseCity,
        data?.warehouseState,
        data?.warehousePincode
      ].filter(Boolean).join(", ") || "-"
    : [
        data?.shippingAddress1, data?.shippingAddress2, data?.shippingAddress3,
        data?.shippingCity, data?.shippingState, data?.shippingPostalCode
      ].filter(Boolean).join(", ") || "-";

  // Helper to determine pricing mode for product display - returns object with mode and hasBothTypes
  const getProductPricingMode = (items) => {
    if (!items || items.length === 0) return { mode: 'one-piece', hasBothTypes: false };
    
    // Check if this is a manual order (marketplace === 'manual' or createdVia === 'manual_admin')
    const isManualOrder = data?.marketplace?.toLowerCase() === 'manual' || data?.createdVia === 'manual_admin';
    
    // Check order type first (only for manual orders)
    if (isManualOrder && data?.orderType) {
      const orderTypeLower = data.orderType.toLowerCase();
      if (orderTypeLower === 'wholesale') {
        return { mode: 'wholesale', hasBothTypes: false };
      } else if (orderTypeLower === 'one-piece') {
        return { mode: 'one-piece', hasBothTypes: false };
      }
    }
    
    const getItemPricingMode = (item) =>
      getOrderItemPricingMode(item, {
        orderBreakdown: data?.breakdown || [],
        orderType: isManualOrder ? data?.orderType : null,
        isManualOrder,
        dropshippingLabel: "one-piece",
      });
    
    // Check pricing modes for all items
    const pricingModes = items.map(item => getItemPricingMode(item));
    const hasOnePiece = pricingModes.includes('one-piece');
    const hasWholesale = pricingModes.includes('wholesale');
    const hasBothTypes = hasOnePiece && hasWholesale;
    
    // Determine main mode
    let mode = 'one-piece';
    if (hasWholesale && !hasOnePiece) {
      mode = 'wholesale';
    } else if (hasBothTypes) {
      mode = 'both';
    }
    
    return { mode, hasBothTypes };
  };

  const groupedProducts = (data?.orderDetails || []).reduce((acc, item) => {
    const offerId = item?.product?.sku || item?.sku || 'unknown';
    if (!acc[offerId]) acc[offerId] = { items: [], product: item?.product };
    acc[offerId].items.push(item);
    return acc;
  }, {});

  return (
    <div className="sod-container">
      {/* Header */}
      <div className="sod-header">
        <div className="sod-header-left">
          <button className="sod-back-btn" onClick={() => navigate('/seller/orders')}>
            <FiArrowLeft size={16} />
          </button>
          <div>
            <h1 className="sod-title">Order Details</h1>
            <p className="sod-order-id">#{data?.upsaleoOrderId || '...'}</p>
          </div>
        </div>
        <div className="sod-header-actions">
          {data.status === "payment pending" && (
            <button className="sod-btn sod-btn-pay" onClick={() => { fetchWalletBalance(); setPaymentModalOpen(true); }}>
              <FiCreditCard size={14} /> Pay Now
            </button>
          )}
          {(data.status === "payment pending" || data.status === "abnormal" || canCancelProcessingOrder()) && (
            <button className="sod-btn sod-btn-cancel" onClick={() => setCancelModalOpen(true)}>Cancel</button>
          )}
          {canRaiseTicket() && (
            <button className="sod-btn sod-btn-ticket" onClick={() => setTicketPopup(true)}>Raise Ticket</button>
          )}
          {data?.ticket && data?.ticketId && (
            <button className="sod-btn sod-btn-view-ticket" onClick={() => setTicketPanelTicketId(data.ticketId)}>View Ticket</button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div className="sod-status-banner" style={{ background: statusConfig.bg }}>
        <div className="sod-status-icon" style={{ background: statusConfig.color }}>
          <statusConfig.icon size={16} color="#fff" />
        </div>
        <div className="sod-status-info">
          <span className="sod-status-label" style={{ color: statusConfig.color }}>{statusConfig.label}</span>
          <span className="sod-status-date">Last updated: {formatDate(data?.updatedAt)}</span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="sod-grid">
        {/* Left Column */}
        <div className="sod-left-col">
          {/* Order Info */}
          <div className="sod-card">
            <div className="sod-card-header">
              <FiFileText />
              <h2>Order Information</h2>
            </div>
            <div className="sod-card-body">
              <div className="sod-info-grid">
                <div className="sod-info-item">
                  <span className="sod-info-label">Importerr Order ID</span>
                  <span className="sod-info-value">{data?.upsaleoOrderId || '-'}</span>
                </div>
                <div className="sod-info-item">
                  <span className="sod-info-label">Marketplace Order ID</span>
                  <span className="sod-info-value">{data?.orderId || '-'}</span>
                </div>
                <div className="sod-info-item">
                  <span className="sod-info-label">Marketplace</span>
                  <span className="sod-info-value sod-badge">{data?.marketplace || '-'}</span>
                </div>
                <div className="sod-info-item">
                  <span className="sod-info-label">Order Date</span>
                  <span className="sod-info-value">{formatDate(data?.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping */}
          <div className="sod-card">
            <div className="sod-card-header">
              <FiTruck />
              <h2>{data?.shippingMethod === 'self-pickup' ? 'Pickup Details' : 'Shipping Details'}</h2>
            </div>
            <div className="sod-card-body">
              {data?.shippingMethod === 'self-pickup' ? (
                // Self Pickup Display
                <>
                  {data?.warehouseName ? (
                    <div className="sod-shipping-info">
                      <div className="sod-recipient">
                        <div className="sod-recipient-avatar" style={{ background: '#10b981' }}>W</div>
                        <div>
                          <p className="sod-recipient-name">{data.warehouseName}</p>
                          {data?.warehousePhone && (
                            <p className="sod-recipient-phone"><FiPhone size={11} /> {data.warehousePhone}</p>
                          )}
                        </div>
                      </div>
                      <div className="sod-address">
                        <FiMapPin />
                        <div>
                          {data?.warehouseAddress1 && <p style={{ margin: '0 0 4px 0' }}>{data.warehouseAddress1}</p>}
                          {data?.warehouseAddress2 && <p style={{ margin: '0 0 4px 0', color: '#6b7280' }}>{data.warehouseAddress2}</p>}
                          <p style={{ margin: '0 0 4px 0' }}>
                            {[data?.warehouseCity, data?.warehouseState, data?.warehousePincode].filter(Boolean).join(', ')}
                          </p>
                          {data?.warehouseLandmark && (
                            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '13px' }}>
                              📍 Landmark: {data.warehouseLandmark}
                            </p>
                          )}
                        </div>
                      </div>
                      {data?.warehouseInstructions && (
                        <div style={{ 
                          background: '#fef3c7', 
                          padding: '12px', 
                          borderRadius: '8px', 
                          marginTop: '12px',
                          borderLeft: '3px solid #f59e0b'
                        }}>
                          <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#92400e' }}>
                            Pickup Instructions:
                          </p>
                          <p style={{ margin: 0, fontSize: '13px', color: '#78350f', lineHeight: '1.5' }}>
                            {data.warehouseInstructions}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p>Warehouse details not available</p>
                    </div>
                  )}
                  <div className="sod-divider"></div>
                  <div className="sod-tracking-info">
                    <div className="sod-tracking-item">
                      <span className="sod-tracking-label">Shipping Method</span>
                      <span className="sod-tracking-value" style={{ color: '#10b981', fontWeight: '600' }}>
                        Self Pickup
                      </span>
                    </div>
                    {data?.warehouseName && (
                      <div className="sod-tracking-item">
                        <span className="sod-tracking-label">Warehouse Name</span>
                        <span className="sod-tracking-value">{data.warehouseName}</span>
                      </div>
                    )}
                    {data?.warehousePhone && (
                      <div className="sod-tracking-item">
                        <span className="sod-tracking-label">Contact Number</span>
                        <span className="sod-tracking-value">{data.warehousePhone}</span>
                      </div>
                    )}
                  </div>
                  {data?.customerNote && (
                    <>
                      <div className="sod-divider" style={{ marginTop: '16px' }}></div>
                      <div style={{ 
                        background: '#f0f9ff', 
                        padding: '12px', 
                        borderRadius: '8px', 
                        marginTop: '12px',
                        borderLeft: '3px solid #3dd0e5'
                      }}>
                        <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: '#0c4a6e' }}>
                          Customer Note:
                        </p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#0c4a6e', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {data.customerNote}
                        </p>
                      </div>
                    </>
                  )}
                </>
              ) : (
                // Regular Shipping Display
                <>
                  <div className="sod-shipping-info">
                    <div className="sod-recipient">
                      <div className="sod-recipient-avatar">{data?.recipientName?.charAt(0)?.toUpperCase() || 'R'}</div>
                      <div>
                        <p className="sod-recipient-name">{data?.recipientName || '-'}</p>
                        <p className="sod-recipient-phone"><FiPhone size={11} /> {data?.shippingPhoneNumber || '-'}</p>
                      </div>
                    </div>
                    <div className="sod-address">
                      <FiMapPin />
                      <p>{shippingAddress}</p>
                    </div>
                  </div>
                  <div className="sod-divider"></div>
                  <div className="sod-tracking-info">
                    <div className="sod-tracking-item">
                      <span className="sod-tracking-label">Shipping Method</span>
                      <span className="sod-tracking-value">
                        {data?.trackingShippingMethod || '-'}
                      </span>
                    </div>
                    <div className="sod-tracking-item">
                      <span className="sod-tracking-label">Carrier</span>
                      <span className="sod-tracking-value">{data?.trackingShippingMethod || '-'}</span>
                    </div>
                    <div className="sod-tracking-item">
                      <span className="sod-tracking-label">Tracking Number</span>
                      <span className="sod-tracking-value sod-tracking-number">{data?.trackingId || '-'}</span>
                    </div>
                  </div>
                  {data?.customerNote && (
                    <>
                      <div className="sod-divider" style={{ marginTop: '16px' }}></div>
                      <div style={{ 
                        background: '#f0f9ff', 
                        padding: '12px', 
                        borderRadius: '8px', 
                        marginTop: '12px',
                        borderLeft: '3px solid #3dd0e5'
                      }}>
                        <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: '#0c4a6e' }}>
                          Customer Note:
                        </p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#0c4a6e', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {data.customerNote}
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="sod-card">
            <div className="sod-card-header">
              <FiClock />
              <h2>Order Timeline</h2>
            </div>
            <div className="sod-card-body">
              <div className="sod-timeline">
                {data?.orderStatus?.map((item, index) => (
                  <div key={item._id} className={`sod-timeline-item ${index === data.orderStatus.length - 1 ? 'sod-timeline-last' : ''}`}>
                    <div className="sod-timeline-dot">
                      <GoCheckCircleFill color="#10b981" />
                    </div>
                    <div className="sod-timeline-content">
                      <p className="sod-timeline-status">{item.status}</p>
                      <p className="sod-timeline-date">{formatDate(item.date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="sod-right-col">
          {/* Order Summary */}
          <div className="sod-card sod-price-card">
            <div className="sod-card-header">
              <FiCreditCard />
              <h2>Order Summary</h2>
            </div>
            <div className="sod-card-body">
              <div className="sod-price-row">
                <span>Subtotal ({data?.orderDetails?.length || 0} items)</span>
                <span>{INR_FORMATTER.format(pricing.productTotal)}</span>
              </div>
              <div className="sod-price-row">
                <span>Shipping Charges</span>
                <span>{pricing.shippingCharges > 0 ? INR_FORMATTER.format(pricing.shippingCharges) : '₹0.00'}</span>
              </div>

              {/* Divider if breakdown exists */}
              {(pricing.imPointsAmount > 0 || pricing.walletAmount > 0 || pricing.razorpayAmount > 0 || pricing.ccavenueAmount > 0 || pricing.bankTransferAmount > 0) && (
                <div className="sod-price-divider"></div>
              )}

              {/* Payment Breakdown items */}
              {pricing.imPointsAmount > 0 && (
                <div className="sod-price-row">
                  <span style={{ color: '#059669', fontWeight: '500' }}>IM Points</span>
                  <span style={{ color: '#059669', fontWeight: '600' }}>{INR_FORMATTER.format(pricing.imPointsAmount)}</span>
                </div>
              )}

              {pricing.walletAmount > 0 && (
                <div className="sod-price-row">
                  <span style={{ color: '#7c3aed', fontWeight: '500' }}>IM Cash</span>
                  <span style={{ color: '#7c3aed', fontWeight: '600' }}>{INR_FORMATTER.format(pricing.walletAmount)}</span>
                </div>
              )}

              {pricing.razorpayAmount > 0 && (
                <div className="sod-price-row">
                  <span style={{ color: '#2563eb', fontWeight: '500' }}>Razorpay</span>
                  <span style={{ color: '#2563eb', fontWeight: '600' }}>{INR_FORMATTER.format(pricing.razorpayAmount)}</span>
                </div>
              )}

              {pricing.ccavenueAmount > 0 && (
                <div className="sod-price-row">
                  <span style={{ color: '#dc2626', fontWeight: '500' }}>CCAvenue</span>
                  <span style={{ color: '#dc2626', fontWeight: '600' }}>{INR_FORMATTER.format(pricing.ccavenueAmount)}</span>
                </div>
              )}

              {pricing.bankTransferAmount > 0 && (
                <div className="sod-price-row">
                  <span style={{ color: '#b45309', fontWeight: '500' }}>Bank Transfer</span>
                  <span style={{ color: '#b45309', fontWeight: '600' }}>{INR_FORMATTER.format(pricing.bankTransferAmount)}</span>
                </div>
              )}

              {/* Legacy fallback if no breakdown but has payment */}
              {!pricing.hasNewBreakdown && pricing.walletAmount === 0 && pricing.razorpayAmount === 0 && pricing.ccavenueAmount === 0 && pricing.bankTransferAmount === 0 && pricing.finalAmount > 0 && (
                <div className="sod-price-row">
                  <span style={{ color: '#4b5563', fontWeight: '500' }}>Paid Amount</span>
                  <span style={{ color: '#4b5563', fontWeight: '600' }}>{INR_FORMATTER.format(pricing.finalAmount)}</span>
                </div>
              )}

              <div className="sod-price-total-box" style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#f0fdf4', 
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #dcfce7'
              }}>
                <span style={{ fontWeight: '700', color: '#166534', fontSize: '14px' }}>Grand Total</span>
                <span style={{ fontWeight: '800', color: '#166534', fontSize: '18px' }}>{INR_FORMATTER.format(pricing.totalOrderValue)}</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="sod-card sod-products-card">
            <div className="sod-card-header">
              <FiShoppingBag />
              <h2>Products ({data?.orderDetails?.length || 0})</h2>
            </div>
            <div className="sod-card-body sod-products-body">
              {Object.entries(groupedProducts).map(([offerId, group]) => {
                const isExpanded = expandedProducts[offerId];
                const firstItem = group.items[0];
                const totalQty = group.items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
                const productImage = firstItem?.variant?.skuImageUrl || firstItem?.image || firstItem?.product?.imgUrl?.[0] || PLACEHOLDER_IMAGE;
                
                return (
                  <div key={offerId} className="sod-product-item">
                    <div className="sod-product-main" onClick={() => toggleProduct(offerId)}>
                      <img src={productImage} alt="" className="sod-product-img" onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }} referrerPolicy="no-referrer" />
                      <div className="sod-product-info">
                        <p className="sod-product-name">{firstItem?.product?.title || firstItem?.productTitle || '-'}</p>
                        <p className="sod-product-sku">SKU: {offerId}</p>
                        <div className="sod-product-meta">
                          <span className="sod-product-qty">Qty: {totalQty}</span>
                          {(() => {
                            const pricingInfo = getProductPricingMode(group.items);
                            if (pricingInfo.hasBothTypes) {
                              return (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                  <span className="sod-product-type" style={{ background: '#ede9fe', color: '#7c3aed' }}>
                                    Wholesale
                                  </span>
                                  <span style={{ margin: '0 2px', color: '#6b7280' }}>+</span>
                                  <span className="sod-product-type" style={{ background: '#dbeafe', color: '#2563eb' }}>
                                    One-piece
                                  </span>
                                </span>
                              );
                            }
                            return (
                              <span className={`sod-product-type ${
                                pricingInfo.mode === 'wholesale' 
                                  ? 'sod-product-type-wholesale' 
                                  : 'sod-product-type-onepiece'
                              }`}>
                                {pricingInfo.mode === 'wholesale' ? 'Wholesale' : 'One-piece'}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <button className="sod-expand-btn" onClick={(e) => { e.stopPropagation(); toggleProduct(offerId); }}>
                        {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                      </button>
                    </div>
                    
                    {isExpanded && (() => {
                      // Calculate pricing for variants (same logic as admin panel)
                      const wholesaleBreakdownMap = new Map();
                      if (Array.isArray(data?.breakdown)) {
                        for (const breakdownItem of data.breakdown) {
                          if (breakdownItem?.pricingMode === 'wholesale' && breakdownItem?.productId && breakdownItem?.breakdown?.[0]?.FBP) {
                            wholesaleBreakdownMap.set(breakdownItem.productId.toString(), Number(breakdownItem.breakdown[0].FBP) || 0);
                          }
                        }
                      }
                      
                      // Check if this is a manual order (marketplace === 'manual' or createdVia === 'manual_admin')
                      const isManualOrder = data?.marketplace?.toLowerCase() === 'manual' || data?.createdVia === 'manual_admin';
                      
                      const getPricingMode = (detail) =>
                        getOrderItemPricingMode(detail, {
                          orderBreakdown: data?.breakdown || [],
                          orderType: isManualOrder ? data?.orderType : null,
                          isManualOrder,
                          dropshippingLabel: "one-piece",
                        });
                      
                      const getOnePiecePrice = (detail) => {
                        const variant = detail?.variant;
                        if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
                          return Number(variant.finalPrice);
                        }
                        const breakdown = variant?.breakdown;
                        return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
                      };
                      
                      // Helper function to get wholesale price - prioritize wholesaleFinalPrice
                      const getWholesalePrice = (detail) => {
                        const variant = detail?.variant;
                        // Priority: wholesaleFinalPrice > breakdown FBP
                        if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) {
                          return Number(variant.wholesaleFinalPrice);
                        }
                        const productId = detail?.product?._id?.toString() || detail?.product?.toString() || '';
                        const wholesaleFBP = wholesaleBreakdownMap.get(productId);
                        if (wholesaleFBP) {
                          // Calculate per-unit price from total FBP
                          let wholesaleQuantity = 0;
                          group.items.forEach(item => {
                            if (getPricingMode(item) === 'wholesale') {
                              wholesaleQuantity += Number(item?.quantity) || 0;
                            }
                          });
                          return wholesaleQuantity > 0 ? wholesaleFBP / wholesaleQuantity : 0;
                        }
                        const breakdown = variant?.breakdown;
                        return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
                      };
                      
                      // Check order type (only for manual orders)
                      const orderType = isManualOrder ? (data?.orderType?.toLowerCase()) : null;
                      const isOrderWholesale = isManualOrder && orderType === 'wholesale';
                      
                      return (
                        <div className="sod-product-variants">
                          {group.items.map((item, idx) => {
                            const variantImg = item?.variant?.skuImageUrl || productImage;
                            const attrs = item?.variant?.skuAttributes || [];
                            const pricingMode = getPricingMode(item);
                            
                            // Calculate price per unit based on pricing mode
                            let pricePerUnit = 0;
                            if (pricingMode === 'one-piece') {
                              pricePerUnit = getOnePiecePrice(item);
                            } else {
                              // Wholesale: use wholesaleFinalPrice if available, otherwise use per-unit FBP
                              if (isManualOrder && isOrderWholesale) {
                                // For manual wholesale orders, use wholesaleFinalPrice directly
                                const variant = item?.variant;
                                if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) {
                                  pricePerUnit = Number(variant.wholesaleFinalPrice);
                                } else {
                                  // Fallback to per-unit FBP calculation
                                  const productId = item?.product?._id?.toString() || item?.product?.toString() || '';
                                  const wholesaleFBP = wholesaleBreakdownMap.get(productId);
                                  let wholesaleQuantity = 0;
                                  group.items.forEach(groupItem => {
                                    if (getPricingMode(groupItem) === 'wholesale') {
                                      wholesaleQuantity += Number(groupItem?.quantity) || 0;
                                    }
                                  });
                                  pricePerUnit = wholesaleFBP && wholesaleQuantity > 0 
                                    ? wholesaleFBP / wholesaleQuantity 
                                    : 0;
                                }
                              } else {
                                // For regular orders, use per-unit FBP
                                const productId = item?.product?._id?.toString() || item?.product?.toString() || '';
                                const wholesaleFBP = wholesaleBreakdownMap.get(productId);
                                let wholesaleQuantity = 0;
                                group.items.forEach(groupItem => {
                                  if (getPricingMode(groupItem) === 'wholesale') {
                                    wholesaleQuantity += Number(groupItem?.quantity) || 0;
                                  }
                                });
                                pricePerUnit = wholesaleFBP && wholesaleQuantity > 0 
                                  ? wholesaleFBP / wholesaleQuantity 
                                  : 0;
                              }
                            }
                            
                            const quantity = Number(item?.quantity) || 0;
                            const totalPrice = pricePerUnit * quantity;
                            
                            const variationSku = item?.sku || item?.variant?.skuId || item?.variant?.sku || '-';
                            return (
                              <div key={idx} className="sod-variant-row">
                                <img src={variantImg} alt="" className="sod-variant-img" onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }} referrerPolicy="no-referrer" />
                                <div className="sod-variant-details">
                                  {attrs.length > 0 && (
                                    <div className="sod-variant-attrs">
                                      {attrs.map((attr, i) => (
                                        <span key={i} className="sod-attr-badge">
                                          {attr?.attributeNameTrans || attr?.attributeName}: {attr?.valueTrans || attr?.value}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <div className="sod-variant-sku">
                                    <span className="sod-variant-sku-label">SkuID:</span>{' '}
                                    <span className="sod-variant-sku-value">{variationSku}</span>
                                  </div>
                                  <div className="sod-variant-price-row">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                      <span>Qty: {quantity}</span>
                                      <span
                                        style={{
                                          padding: '2px 8px',
                                          borderRadius: '4px',
                                          fontSize: '11px',
                                          fontWeight: '500',
                                          background: pricingMode === 'wholesale' ? '#ede9fe' : '#dbeafe',
                                          color: pricingMode === 'wholesale' ? '#7c3aed' : '#2563eb'
                                        }}
                                      >
                                        {pricingMode === 'wholesale' ? 'Wholesale' : 'One-piece'}
                                      </span>
                                    </div>
                                    <span className="sod-variant-price">{INR_FORMATTER.format(totalPrice)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <RaiseTicket modalIsOpen={ticketPopup} setIsOpen={setTicketPopup} id={data?._id} handleRefresh={getOrderById} />
      <PaymentModal isOpen={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} order={data} orders={null} onConfirm={handlePaymentConfirm} isLoading={isPayingOrder} walletBalance={walletBalance} />
      <CancelOrderModal isOpen={cancelModalOpen} onClose={() => setCancelModalOpen(false)} order={data} orders={null} onConfirm={handleCancelConfirm} isLoading={isCancelling} />

      {/* Ticket chat panel (right side) */}
      {ticketPanelTicketId && (
        <TicketChatPanel
          ticketId={ticketPanelTicketId}
          orderLabel={data?.upsaleoOrderId}
          onClose={() => setTicketPanelTicketId(null)}
        />
      )}
    </div>
  );
}
